import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import ReactCardFlip from 'react-card-flip';
import { AuthContext } from '../App';
import { ArrowLeft, Trophy, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import './FlashcardGame.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOUNDS = {
  flip: 'https://assets.mixkit.co/active_storage/sfx/2000/2000-preview.mp3',
  victory: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3'
};

function FlashcardGame() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setIdFromUrl = searchParams.get('setId');
  
  const { user } = React.useContext(AuthContext);
  const [sets, setSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => { 
    loadSets(); 
  }, []);
  
  useEffect(() => {
    if (gameStarted && timeLeft !== null && timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft(prev => prev <= 1 ? 0 : prev - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [gameStarted, timeLeft]);
  
  // Auto-start game if setId is in URL
  useEffect(() => {
    if (setIdFromUrl && sets.length > 0 && !gameStarted) {
      const targetSet = sets.find(s => s.id === setIdFromUrl);
      if (targetSet) {
        startGame(targetSet);
      }
    }
  }, [setIdFromUrl, sets]);
  
  const loadSets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/flashcards`, { withCredentials: true });
      setSets(response.data || []);
    } catch (error) {
      console.error('Failed to load sets:', error);
      toast.error('Failed to load flashcard sets');
    } finally {
      setLoading(false);
    }
  };
  
  const playSound = (soundName) => {
    try {
      const audio = new Audio(SOUNDS[soundName]);
      audio.volume = 0.3;
      audio.play().catch(e => console.log('Sound play failed:', e));
    } catch (error) {}
  };
  
  const startGame = (set) => {
    if (!set.cards || set.cards.length === 0) {
      toast.error('This set has no cards!');
      return;
    }
    setSelectedSet(set);
    setGameStarted(true);
    setCurrentIndex(0);
    setIsFlipped(false);
    setScore(0);
    if (set.timer_setting === '30s') setTimeLeft(30);
    else if (set.timer_setting === '1min') setTimeLeft(60);
    else setTimeLeft(null);
  };
  
  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    playSound('flip');
    if (!isFlipped) setScore(score + 5);
  };
  
  const nextCard = () => {
    if (currentIndex < selectedSet.cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    } else {
      playSound('victory');
      toast.success('🎉 All cards completed!');
    }
  };
  
  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };
  
  const resetGame = () => {
    setGameStarted(false);
    setSelectedSet(null);
    setCurrentIndex(0);
    setIsFlipped(false);
    setScore(0);
    setTimeLeft(null);
    // Navigate back to hub if came from URL
    if (setIdFromUrl) {
      navigate('/flashcard-hub');
    }
  };
  
  if (loading) {
    return (
      <div className="flashcard-game-page" data-testid="flashcard-game">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading flashcard sets...</p>
        </div>
      </div>
    );
  }
  
  if (!gameStarted) {
    return (
      <div className="flashcard-game-page" data-testid="flashcard-game">
        <div className="sets-container">
          <div className="sets-header">
            <Button 
              onClick={() => navigate('/flashcard-hub')} 
              variant="ghost" 
              className="back-to-hub"
              data-testid="back-to-hub"
            >
              <ArrowLeft size={20} />
              Back to Hub
            </Button>
            <h1>📚 Flashcard Sets</h1>
          </div>
          <div className="sets-grid">
            {sets.length === 0 ? (
              <p className="no-sets">No flashcard sets available yet!</p>
            ) : (
              sets.map((set) => (
                <motion.div 
                  key={set.id} 
                  className="set-card" 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  whileHover={{ scale: 1.02 }}
                  data-testid={`set-card-${set.id}`}
                >
                  <div className="set-info">
                    <h3>{set.title}</h3>
                    <div className="set-details">
                      <span className="detail-badge">{set.cards?.length || 0} Cards</span>
                      {set.timer_setting !== 'off' && (
                        <span className="detail-badge timer-badge">
                          <Clock size={14} />{set.timer_setting}
                        </span>
                      )}
                    </div>
                    <Button 
                      onClick={() => startGame(set)} 
                      className="play-btn"
                      data-testid={`study-btn-${set.id}`}
                    >
                      Study Now
                    </Button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }
  
  const currentCard = selectedSet.cards[currentIndex];
  
  return (
    <div className="flashcard-game-page" data-testid="flashcard-game-playing">
      <div className="game-container">
        <div className="game-header">
          <Button 
            onClick={resetGame} 
            className="back-btn" 
            variant="ghost"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />Back
          </Button>
          <div className="game-stats">
            <div className="stat">
              <Trophy size={20} />
              <span>{score} pts</span>
            </div>
            {timeLeft !== null && (
              <div className="stat timer">
                <Clock size={20} />
                <span>{timeLeft}s</span>
              </div>
            )}
          </div>
        </div>
        
        <h2 className="game-title">{selectedSet.title}</h2>
        
        <div className="card-progress">Card {currentIndex + 1} of {selectedSet.cards.length}</div>
        
        <div className="card-container">
          <ReactCardFlip isFlipped={isFlipped} flipDirection="horizontal">
            <div className="flashcard front" onClick={handleFlip} data-testid="flashcard-front">
              <div className="card-content">
                <div className="card-label">Front</div>
                <p>{currentCard.front_text}</p>
                {currentCard.front_image && (
                  <img src={currentCard.front_image} alt="Front" className="card-img" />
                )}
                <div className="flip-hint">Click to flip 🔄</div>
              </div>
            </div>
            <div className="flashcard back" onClick={handleFlip} data-testid="flashcard-back">
              <div className="card-content">
                <div className="card-label">Back</div>
                <p>{currentCard.back_text}</p>
                {currentCard.back_image && (
                  <img src={currentCard.back_image} alt="Back" className="card-img" />
                )}
                <div className="flip-hint">Click to flip 🔄</div>
              </div>
            </div>
          </ReactCardFlip>
        </div>
        
        <div className="card-controls">
          <Button 
            onClick={prevCard} 
            disabled={currentIndex === 0} 
            className="nav-btn"
            data-testid="prev-btn"
          >
            <ChevronLeft size={24} />Previous
          </Button>
          <Button 
            onClick={nextCard} 
            disabled={currentIndex === selectedSet.cards.length - 1} 
            className="nav-btn"
            data-testid="next-btn"
          >
            Next<ChevronRight size={24} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FlashcardGame;
